import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../utils/api';
import {
  Layout,
  Button,
  Typography,
  Card,
  Tag,
  Modal,
  Input,
  message,
} from 'antd';
import {
  HeartOutlined,
  UserOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  TeamOutlined,
  FileTextOutlined,
  CloseOutlined,
  ShopOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import './IntroPage.css';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

const IntroPage = () => {
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authCode, setAuthCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingTestType, setPendingTestType] = useState(null); // 记录用户点击的测试类型
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showScienceModal, setShowScienceModal] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [showBackTop, setShowBackTop] = useState(false);
  const [completedTests, setCompletedTests] = useState({
    self: false,
    partner: false
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    
    const handleScroll = () => {
      setShowBackTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    
    // 检查是否有session，如果有则检查是否有历史报告
    const sessionId = localStorage.getItem('sessionId');
    if (sessionId) {
      // 检查是否有历史报告
      const selfReports = JSON.parse(localStorage.getItem(`rpi_reports_self_${sessionId}`) || '[]');
      const partnerReports = JSON.parse(localStorage.getItem(`rpi_reports_partner_${sessionId}`) || '[]');
      setCompletedTests({
        self: selfReports.length > 0,
        partner: partnerReports.length > 0
      });
    }
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 检测是否是移动端
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // 打开小红书店铺/商品
  const openXiaohongshu = () => {
    const mobile = isMobile();
    // 小红书商品分享链接
    const xiaohongshuLink = process.env.REACT_APP_XIAOHONGSHU_PRODUCT_URL || 'https://xhslink.com/m/47wsB4VLL30';
    
    if (mobile) {
      // 移动端：直接使用分享链接，手机会自动提示打开 App
      window.location.href = xiaohongshuLink;
    } else {
      // 电脑端：在新标签页打开
      window.open(xiaohongshuLink, '_blank');
    }
  };

  const handleVerifyCode = async () => {
    if (!authCode.trim()) {
      message.error('请输入授权码');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.verifyCode(authCode.trim());
      
      if (response.success) {
        localStorage.setItem('sessionId', response.sessionId);
        message.success('授权码验证成功');
        setShowAuthModal(false);
        setAuthCode('');
        
        // 验证成功后，继续之前的测试流程
        if (pendingTestType) {
          const completed = localStorage.getItem(`rpi_completed_${pendingTestType}`);
          if (completed === 'true') {
            navigate(`/report/${pendingTestType}`);
          } else {
            navigate(`/consent/${pendingTestType}`);
          }
        }
      } else {
        message.error(response.message || '授权码验证失败');
      }
    } catch (err) {
      message.error(err.message || '授权码验证失败');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = (testType) => {
    console.log('=== 开始测试 ===');
    console.log('测试类型:', testType);
    
    const sessionId = localStorage.getItem('sessionId');
    console.log('当前 sessionId:', sessionId);
    
    if (!sessionId) {
      // 没有 sessionId，弹出授权码输入框
      console.log('没有 sessionId，弹出授权码输入框');
      setPendingTestType(testType);
      setShowAuthModal(true);
      return;
    }

    // 清除当前报告ID，开始新测试
    console.log('有 sessionId，清除当前报告ID，开始新测试');
    localStorage.removeItem('currentReportId');
    
    // 直接跳转到知情同意书，开始新测试（允许重复测试）
    navigate(`/consent/${testType}`);
  };

  const handleViewReports = (testType) => {
    // 查看该类型的所有历史报告
    navigate(`/my-reports`);
  };

  const scrollToTestSelection = () => {
    const element = document.getElementById('test-selection');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Layout className="rpi-intro-layout">
      {/* 顶部导航栏 */}
      <header className="rpi-header">
        <div className="rpi-header-content">
          <div className="rpi-logo">
            <div className="rpi-logo-icon">💕</div>
            <div className="rpi-logo-text">
              <div className="rpi-logo-title">RPI Calculator</div>
              <div className="rpi-logo-subtitle">恋爱占有欲指数计算器</div>
            </div>
          </div>
          <nav className="rpi-nav">
            <a 
              href="#my-reports" 
              className="rpi-nav-link"
              onClick={(e) => {
                e.preventDefault();
                navigate('/my-reports');
              }}
            >
              <span className="rpi-nav-icon">📊</span>
              我的测试报告
            </a>
            <a 
              href="#guide" 
              className="rpi-nav-link"
              onClick={(e) => {
                e.preventDefault();
                setShowGuideModal(true);
              }}
            >
              <span className="rpi-nav-icon">📖</span>
              使用指南
            </a>
            <a 
              href="#scientific" 
              className="rpi-nav-link"
              onClick={(e) => {
                e.preventDefault();
                setShowScienceModal(true);
              }}
            >
              <span className="rpi-nav-icon">🔬</span>
              科学依据
            </a>
          </nav>
        </div>
      </header>

      {/* 背景装饰 */}
      <div className="rpi-background-decorations">
        <div className="rpi-heart-decoration rpi-heart-1">💕</div>
        <div className="rpi-heart-decoration rpi-heart-2">💖</div>
        <div className="rpi-heart-decoration rpi-heart-3">💗</div>
        <div className="rpi-heart-decoration rpi-heart-4">💓</div>
        <div className="rpi-heart-decoration rpi-heart-5">💝</div>
      </div>

      <Content className="rpi-intro-content">
        {/* Hero Section */}
        <div className="rpi-hero-section">
          <div className="rpi-hero-badge">
            <span className="rpi-badge-icon">💕</span>
            基于心理学研究的专业测评工具
          </div>

          <Title level={1} className="rpi-main-title">
            恋爱占有欲指数计算器
          </Title>
          <Paragraph className="rpi-main-description">
            专业的恋爱心理评估工具，基于多个经过验证的心理测量量表，
            帮助您科学地了解自己或恋人的占有欲程度，促进健康和谐的亲密关系发展。
          </Paragraph>

          {/* 双视角智能评估卡片 */}
          <Card className="rpi-feature-card">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div className="rpi-feature-icon-bg">
                <TeamOutlined style={{ fontSize: 24, color: 'var(--rpi-primary)' }} />
              </div>
              <div className="rpi-feature-content">
                <Title level={4}>双视角智能评估</Title>
                <Paragraph>
                  提供"给自己测"和"为恋人测"两种视角，系统会根据您的年龄、星座、恋爱状态等因素，
                  提供个性化的分析结果和建议，帮助您更全面地认识恋爱关系中的占有欲特点。
                </Paragraph>
              </div>
            </div>
          </Card>

          {/* 主要行动按钮 */}
          <div className="rpi-main-actions">
            <Button
              type="primary"
              size="large"
              className="rpi-btn-primary"
              onClick={() => handleStartTest('self')}
            >
              <span className="rpi-btn-icon">💖</span>
              {completedTests.self ? '再次测试（给自己）' : '给自己测'}
              <ArrowRightOutlined />
            </Button>
            <Button
              size="large"
              className="rpi-btn-secondary"
              onClick={() => handleStartTest('partner')}
            >
              <span className="rpi-btn-icon">💞</span>
              {completedTests.partner ? '再次测试（为恋人）' : '为恋人测'}
              <ArrowRightOutlined />
            </Button>
          </div>

          {/* 特点展示 */}
          <div className="rpi-stats-row">
            <div className="rpi-stat-item">
              <ClockCircleOutlined />
              <div className="rpi-stat-number">10-15</div>
              <div className="rpi-stat-label">分钟完成</div>
            </div>
            <div className="rpi-stat-item">
              <SafetyOutlined />
              <div className="rpi-stat-number">4</div>
              <div className="rpi-stat-label">核心维度</div>
            </div>
            <div className="rpi-stat-item">
              <CheckCircleOutlined />
              <div className="rpi-stat-number">100%</div>
              <div className="rpi-stat-label">隐私保护</div>
            </div>
            <div className="rpi-stat-item">
              <TeamOutlined />
              <div className="rpi-stat-number">双视角</div>
              <div className="rpi-stat-label">深度分析</div>
            </div>
          </div>
        </div>

        {/* 选择测评视角 Section */}
        <div id="test-selection" className="rpi-section rpi-test-selection">
          <div className="rpi-section-header">
            <Title level={2}>选择测评视角</Title>
            <Paragraph className="rpi-section-subtitle">
              提供两种测评视角，您可以评估自己或帮助了解恋人的占有欲特点
            </Paragraph>
          </div>

          <div className="rpi-test-cards">
            {/* 给自己测 */}
            <Card
              className={`rpi-test-card ${completedTests.self ? 'rpi-test-card-completed' : 'rpi-test-card-pending'} ${hoveredCard === 'self' ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredCard('self')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="rpi-test-card-badge">
                <span className="rpi-badge-sparkle">✨</span>
                推荐
              </div>

              <div className="rpi-test-card-header">
                <div className="rpi-test-icon-wrapper">
                  <UserOutlined className="rpi-test-icon" />
                </div>
                <div>
                  <Title level={3}>给自己测</Title>
                  <Text type="secondary">深入了解自己的占有欲程度</Text>
                </div>
              </div>

              <div className="rpi-test-dimensions">
                <div className="rpi-dimension-item">
                  <CheckCircleOutlined />
                  <Text>控制欲望量表</Text>
                </div>
                <div className="rpi-dimension-item">
                  <CheckCircleOutlined />
                  <Text>嫉妒强度量表</Text>
                </div>
                <div className="rpi-dimension-item">
                  <CheckCircleOutlined />
                  <Text>情感依赖量表</Text>
                </div>
                <div className="rpi-dimension-item">
                  <CheckCircleOutlined />
                  <Text>关系不安全感量表</Text>
                </div>
              </div>

              <div className="rpi-test-info">
                <div className="rpi-test-info-item">
                  <Text type="secondary">预计用时</Text>
                  <Text strong className="rpi-time-highlight">10-15 分钟</Text>
                </div>
                <div className="rpi-test-info-item">
                  <Text type="secondary">题目数量</Text>
                  <Text strong>40 题</Text>
                </div>
              </div>

              <Button
                type="primary"
                size="large"
                block
                icon={<ArrowRightOutlined />}
                className="rpi-test-start-btn-secondary"
                onClick={() => handleStartTest('self')}
              >
                {completedTests.self ? '💖 再次测试' : '💖 给自己测'}
              </Button>
            </Card>

            {/* 为恋人测 */}
            <Card
              className={`rpi-test-card ${completedTests.partner ? 'rpi-test-card-completed' : 'rpi-test-card-pending'} ${hoveredCard === 'partner' ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredCard('partner')}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="rpi-test-card-header">
                <div className="rpi-test-icon-wrapper rpi-test-icon-partner">
                  <HeartOutlined className="rpi-test-icon" />
                </div>
                <div>
                  <Title level={3}>为恋人测</Title>
                  <Text type="secondary">了解恋人对您的占有欲程度</Text>
                </div>
              </div>

              <div className="rpi-test-dimensions">
                <div className="rpi-dimension-item">
                  <CheckCircleOutlined />
                  <Text>恋人控制欲望量表</Text>
                </div>
                <div className="rpi-dimension-item">
                  <CheckCircleOutlined />
                  <Text>恋人嫉妒强度量表</Text>
                </div>
                <div className="rpi-dimension-item">
                  <CheckCircleOutlined />
                  <Text>恋人情感依赖量表</Text>
                </div>
                <div className="rpi-dimension-item">
                  <CheckCircleOutlined />
                  <Text>恋人关系不安全感量表</Text>
                </div>
              </div>

              <div className="rpi-test-info">
                <div className="rpi-test-info-item">
                  <Text type="secondary">预计用时</Text>
                  <Text strong className="rpi-time-highlight">10-15 分钟</Text>
                </div>
                <div className="rpi-test-info-item">
                  <Text type="secondary">题目数量</Text>
                  <Text strong>40 题</Text>
                </div>
              </div>

              <Button
                type="primary"
                size="large"
                block
                icon={<ArrowRightOutlined />}
                className="rpi-test-start-btn-secondary"
                onClick={() => handleStartTest('partner')}
              >
                {completedTests.partner ? '💞 再次测试' : '💞 为恋人测'}
              </Button>
            </Card>
          </div>
        </div>

        {/* 科学可靠的评估基础 Section */}
        <div className="rpi-section rpi-scientific-basis">
          <div className="rpi-section-header">
            <Title level={2}>科学可靠的评估基础</Title>
            <Paragraph className="rpi-section-subtitle">
              基于心理学研究的专业量表，全面评估恋爱占有欲的四个核心维度
            </Paragraph>
          </div>

          <div className="rpi-dimension-cards">
            <Card className="rpi-dimension-card">
              <div className="rpi-dimension-icon">
                <UserOutlined style={{ color: 'var(--rpi-primary)' }} />
              </div>
              <Title level={4}>控制欲望</Title>
              <Paragraph>评估在恋爱关系中对伴侣的控制倾向和监控行为</Paragraph>
            </Card>
            <Card className="rpi-dimension-card">
              <div className="rpi-dimension-icon">
                <HeartOutlined style={{ color: 'var(--rpi-primary)' }} />
              </div>
              <Title level={4}>嫉妒强度</Title>
              <Paragraph>测量在恋爱关系中的嫉妒情绪和排他性倾向</Paragraph>
            </Card>
            <Card className="rpi-dimension-card">
              <div className="rpi-dimension-icon">
                <TeamOutlined style={{ color: 'var(--rpi-primary)' }} />
              </div>
              <Title level={4}>情感依赖</Title>
              <Paragraph>评估对伴侣的情感依赖程度和独立性水平</Paragraph>
            </Card>
            <Card className="rpi-dimension-card">
              <div className="rpi-dimension-icon">
                <SafetyOutlined style={{ color: 'var(--rpi-primary)' }} />
              </div>
              <Title level={4}>关系不安</Title>
              <Paragraph>测量在亲密关系中的不安全感和焦虑程度</Paragraph>
            </Card>
          </div>
        </div>

        {/* 隐私保护 Section */}
        <div className="rpi-section rpi-privacy-section">
          <Card className="rpi-privacy-card">
            <div className="rpi-privacy-icon">
              <SafetyOutlined style={{ fontSize: 60, color: 'var(--rpi-success)' }} />
            </div>
            <Title level={2}>您的隐私是我们的首要关注</Title>
            <Paragraph className="rpi-privacy-description">
              所有评估数据仅在本地处理和存储。
              您拥有完全的数据控制权，可以随时删除评估历史。
            </Paragraph>
            <div className="rpi-privacy-features">
              <Tag icon={<CheckCircleOutlined />} color="success" className="rpi-privacy-feature">本地数据存储</Tag>
              <Tag icon={<CheckCircleOutlined />} color="success" className="rpi-privacy-feature">完全匿名化</Tag>
              <Tag icon={<CheckCircleOutlined />} color="success" className="rpi-privacy-feature">可随时删除</Tag>
            </div>
          </Card>
        </div>

        {/* CTA Section */}
        <div className="rpi-section rpi-cta-section">
          <Title level={2}>准备好探索恋爱关系的真相了吗？</Title>
          <Paragraph className="rpi-cta-description">
            通过科学的评估工具，深入了解恋爱中的占有欲特点，促进更健康和谐的亲密关系。
          </Paragraph>
          <div className="rpi-cta-actions">
            <Button
              type="primary"
              size="large"
              className="rpi-cta-btn-primary"
              onClick={scrollToTestSelection}
            >
              <span className="rpi-btn-icon">🚀</span>
              立即开始测评
            </Button>
          </div>
        </div>

        {/* Footer */}
        <footer className="rpi-footer">
          <div className="rpi-footer-content">
            <div className="rpi-footer-simple">
              <div className="rpi-footer-logo">
                <div className="rpi-footer-logo-icon">💕</div>
                <div className="rpi-footer-logo-text">RPI Calculator</div>
              </div>
              <Paragraph className="rpi-footer-description">
                基于心理学研究的恋爱占有欲指数计算器，帮助您更好地了解恋爱关系中的占有欲特点。
              </Paragraph>
            </div>
            <div className="rpi-footer-bottom">
              <div className="rpi-footer-copyright">
                <span className="rpi-copyright-icon">💝</span>
                <Text className="rpi-footer-copyright-text">
                  © 2025 恋爱占有欲指数计算器. 仅供教育和自我了解使用，不能替代专业心理健康服务。
                </Text>
              </div>
            </div>
          </div>
        </footer>
      </Content>

      {/* 移动端浮动按钮 - 我的测试报告 */}
      <div className="rpi-mobile-reports-button" onClick={() => navigate('/my-reports')}>
        <span className="rpi-mobile-reports-icon">📊</span>
        <span className="rpi-mobile-reports-text">我的报告</span>
      </div>

      {/* 授权码验证Modal */}
      <Modal
        title="请输入授权码"
        open={showAuthModal}
        onOk={handleVerifyCode}
        onCancel={() => {
          setShowAuthModal(false);
          setAuthCode('');
          setPendingTestType(null);
        }}
        confirmLoading={loading}
        okText="开始测试"
        cancelText="取消"
        width={520}
      >
        <div style={{ marginTop: 16, marginBottom: 24 }}>
          <Input
            placeholder="请输入8位授权码"
            value={authCode}
            onChange={(e) => setAuthCode(e.target.value.toUpperCase())}
            maxLength={8}
            size="large"
            onPressEnter={handleVerifyCode}
            autoFocus
          />
        <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
          请输入8位授权码以开始测试
        </div>
        </div>

        {/* 购买引导区域 */}
        <div style={{
          background: 'linear-gradient(135deg, #FFF5F8 0%, #FFE4E9 100%)',
          borderRadius: 12,
          padding: '20px',
          marginTop: 16
        }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 16,
          gap: 8
        }}>
          <ShoppingOutlined style={{ fontSize: 20, color: '#E92063' }} />
          <span style={{ fontSize: 16, fontWeight: 'bold', color: '#333' }}>
            获取授权码
          </span>
        </div>
        
        <div style={{ fontSize: 14, color: '#666', marginBottom: 16, lineHeight: 1.6 }}>
          测试授权码可以在以下平台获取：
        </div>

          <div style={{ display: 'flex' }}>
            <Button
              type="primary"
              icon={<ShopOutlined />}
              size="large"
              style={{
                flex: 1,
                height: 48,
                background: '#FF2442',
                borderColor: '#FF2442',
                fontSize: 15,
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(255, 36, 66, 0.3)'
              }}
              onClick={openXiaohongshu}
            >
              小红书店铺
            </Button>
          </div>

          <div style={{
            marginTop: 16,
            padding: '12px',
            background: 'rgba(255, 255, 255, 0.8)',
            borderRadius: 8,
            fontSize: 12,
            color: '#666',
            lineHeight: 1.6
          }}>
            <div style={{ marginBottom: 4 }}>
              💡 <strong>温馨提示：</strong>
            </div>
            <div>• 授权码购买后立即可用，一码一测</div>
            <div>• 测试结果保存在本地，可随时查看</div>
            <div>• 支持"给自己测"和"为恋人测"两种模式</div>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default IntroPage;
